import React from 'react'
import classNames from 'classnames'
import { t } from 'i18next'
import { IllustrationName } from '@pandiafi/design-system'
import { StatusView } from '@pandiafi/components'

import { ResidentAppIllustration } from 'components/ResidentAppIllustration'

interface ErrorProps {
  title?: string
  message?: string
  illustration?: IllustrationName
  eventId?: string
  action?: React.ReactNode
  /** One below the surrounding heading, e.g. 3 inside a titled card */
  headingLevel?: 2 | 3 | 4
  className?: string
}

/**
 * An error in place of the content. The title, or the message when there is
 * no title, is the heading. Without either, e.g. on `ErrorPage` whose h1
 * already says what went wrong, only the illustration and action show.
 */
export const Error: React.FC<ErrorProps> = ({
  title,
  message,
  action,
  eventId,
  illustration = 'house-unhappy',
  headingLevel = 2,
  className,
}) => {
  const heading = title ?? message
  const illustrationElement = (
    <ResidentAppIllustration name={illustration} alt="" />
  )
  const eventIdText = eventId && (
    <p className="mt-10">{t('errors:other.eventId', { id: eventId })}</p>
  )

  if (!heading) {
    return (
      <div
        className={classNames(
          'flex flex-col items-center gap-6 text-center',
          className
        )}
      >
        <ResidentAppIllustration
          name={illustration}
          alt=""
          className="h-40 max-h-40"
        />
        {action}
        {eventIdText}
      </div>
    )
  }

  return (
    <StatusView
      variant="error"
      title={heading}
      headingLevel={headingLevel}
      illustration={illustrationElement}
      action={action}
      className={className}
    >
      {title && message}
      {eventIdText}
    </StatusView>
  )
}
